import React, { useState } from 'react';
import { toast } from 'react-toastify';
import './FeedbackModal.css';

const FeedbackModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    userName: '',
    userEmail: '',
    userPhone: '',
    rating: 5,
    category: 'service',
    comment: '',
    avatar: '👤'
  });

  const [errors, setErrors] = useState({});

  const categories = [
    { id: 'service', label: 'Service Quality', icon: '👔' },
    { id: 'food', label: 'Food Quality', icon: '🍽️' },
    { id: 'cleanliness', label: 'Cleanliness', icon: '🧹' },
    { id: 'value', label: 'Value for Money', icon: '💰' },
    { id: 'ambiance', label: 'Ambiance', icon: '✨' }
  ];

  const avatarOptions = ['👤', '👨', '👩', '👨‍💼', '👩‍💼'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRatingChange = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  const handleCategoryChange = (category) => {
    setFormData(prev => ({
      ...prev,
      category
    }));
  };

  const handleAvatarChange = (avatar) => {
    setFormData(prev => ({
      ...prev,
      avatar
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.userName.trim()) {
      newErrors.userName = 'Name is required';
    }

    if (!formData.userEmail.trim()) {
      newErrors.userEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.userEmail)) {
      newErrors.userEmail = 'Please enter a valid email';
    }

    if (!formData.userPhone.trim()) {
      newErrors.userPhone = 'Phone number is required';
    }

    if (!formData.comment.trim()) {
      newErrors.comment = 'Please share your feedback';
    } else if (formData.comment.trim().length < 10) {
      newErrors.comment = 'Feedback must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      onSave(formData);
      setFormData({
        userName: '',
        userEmail: '',
        userPhone: '',
        rating: 5,
        category: 'service',
        comment: '',
        avatar: '👤'
      });
      toast.success('Feedback submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit feedback. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Submit Feedback</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="feedback-form">
          {/* User Info Section */}
          <div className="form-section">
            <h3>Your Information</h3>

            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                name="userName"
                value={formData.userName}
                onChange={handleInputChange}
                placeholder="Your full name"
                className={errors.userName ? 'error' : ''}
              />
              {errors.userName && <span className="error-text">{errors.userName}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="userEmail"
                  value={formData.userEmail}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  className={errors.userEmail ? 'error' : ''}
                />
                {errors.userEmail && <span className="error-text">{errors.userEmail}</span>}
              </div>

              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  name="userPhone"
                  value={formData.userPhone}
                  onChange={handleInputChange}
                  placeholder="+1 (234) 567-8900"
                  className={errors.userPhone ? 'error' : ''}
                />
                {errors.userPhone && <span className="error-text">{errors.userPhone}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>Choose Avatar</label>
              <div className="avatar-selector">
                {avatarOptions.map(avatar => (
                  <button
                    key={avatar}
                    type="button"
                    className={`avatar-btn ${formData.avatar === avatar ? 'active' : ''}`}
                    onClick={() => handleAvatarChange(avatar)}
                  >
                    {avatar}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Rating Section */}
          <div className="form-section">
            <h3>Your Rating</h3>

            <div className="form-group">
              <label>Rate Your Experience *</label>
              <div className="rating-selector">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    className={`rating-star ${formData.rating >= star ? 'active' : ''}`}
                    onClick={() => handleRatingChange(star)}
                    title={`${star} star${star !== 1 ? 's' : ''}`}
                  >
                    ★
                  </button>
                ))}
              </div>
              <div className="rating-labels">
                <span className="rating-label poor">Poor</span>
                <span className="rating-label average">Average</span>
                <span className="rating-label excellent">Excellent</span>
              </div>
            </div>
          </div>

          {/* Category Section */}
          <div className="form-section">
            <h3>Feedback Category</h3>

            <div className="form-group">
              <label>What is your feedback about? *</label>
              <div className="category-selector">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    className={`category-btn ${formData.category === cat.id ? 'active' : ''}`}
                    onClick={() => handleCategoryChange(cat.id)}
                  >
                    <span className="category-icon">{cat.icon}</span>
                    <span className="category-label">{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Comment Section */}
          <div className="form-section">
            <h3>Your Feedback</h3>

            <div className="form-group">
              <label>Comments *</label>
              <textarea
                name="comment"
                value={formData.comment}
                onChange={handleInputChange}
                placeholder="Share your experience in detail (minimum 10 characters)..."
                rows="5"
                className={errors.comment ? 'error' : ''}
              />
              <div className="char-count">
                {formData.comment.length}/500 characters
              </div>
              {errors.comment && <span className="error-text">{errors.comment}</span>}
            </div>
          </div>

          {/* Actions */}
          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-submit">
              Submit Feedback
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackModal;
